/**
 * Storage problems, in words. NFR-05, NFR-07, R-04.
 *
 * Every one of these is a state a local-first app can genuinely reach, and each
 * one is silent by default — which is the problem. A browser that refuses
 * IndexedDB, or that evicted the database last week, produces an app that looks
 * like it is working and quietly remembers nothing. R-04 rates that as high
 * probability and high impact, and it is worst on iOS.
 *
 * The wording is deliberately plain and does not blame the user or the browser.
 * Someone reading this may already be having a bad day.
 */
import { useNavigate } from 'react-router-dom'
import { t } from '@copy/index'
import { useEntries } from '@contexts/index'
import { Notice } from '@shared/ui'

export function StorageNotices() {
  const { state, storage, writeProblem, reload, dismissWriteProblem } = useEntries()
  const navigate = useNavigate()

  const notices: React.ReactNode[] = []

  if (storage?.availability === 'unavailable') {
    // The most serious case: nothing will be saved at all. It goes first and it
    // does not get a dismiss button, because dismissing it would leave someone
    // typing into a void.
    notices.push(
      <Notice key="unavailable" tone="error">
        {t('storage.unavailable')}
      </Notice>,
    )
  }

  if (state === 'error') {
    notices.push(
      <Notice
        key="load"
        tone="error"
        action={{ label: t('common.retry'), onClick: () => void reload() }}
      >
        {t('storage.loadFailed')}
      </Notice>,
    )
  }

  if (writeProblem !== undefined) {
    if (writeProblem === 'premium') {
      notices.push(
        <Notice
          key="write"
          tone="warning"
          action={{
            label: t('storage.upgrade'),
            onClick: () => {
              dismissWriteProblem()
              navigate('/data')
            },
          }}
        >
          {t('storage.saveFailedPremium')}
        </Notice>,
      )
    } else {
      notices.push(
        <Notice
          key="write"
          tone="error"
          action={{ label: t('storage.dismiss'), onClick: dismissWriteProblem }}
        >
          {writeProblem === 'quota' ? t('storage.saveFailedQuota') : t('storage.saveFailed')}
        </Notice>,
      )
    }
  }

  if (storage?.possibleEviction === true) {
    notices.push(
      <Notice key="eviction" tone="warning">
        {t('storage.possibleEviction')}
      </Notice>,
    )
  }

  if (notices.length === 0) return null

  return (
    <div className="flex flex-col gap-2 px-4 pt-4">
      {notices}
    </div>
  )
}
